import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input, Toggle } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatDate } from '../../lib/utils'
import type { ConfiguracaoNotificacoes } from '../../types'

// Usadas só se o servidor não mandar a faixa (spec: 6 a 22, padrão 9).
const FAIXA_PADRAO = { minimo: 6, maximo: 22, padrao: 9, aplicaNaProximaSubida: false }

/** Admin: horário (Brasília) e liga/desliga da rotina diária de avisos. */
export function NotificacoesConfigPage() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['admin-notificacoes'], queryFn: adminApi.notificacoesConfiguracao })
  return (
    <>
      <PageHeader title="Notificações" />
      {isLoading ? <Card><LoadingState /></Card> : isError || !data ? <Card><ErrorState message={(error as Error)?.message || 'Não foi possível carregar a configuração.'} /></Card> : <Formulario key={data.atualizadoEm ?? 'inicial'} config={data} />}
    </>
  )
}

function Formulario({ config }: { config: ConfiguracaoNotificacoes }) {
  const qc = useQueryClient()
  const toast = useToast()
  const faixa = config.faixas?.horaDiariaBrt ?? FAIXA_PADRAO
  const [hora, setHora] = useState(String(config.horaDiariaBrt ?? faixa.padrao))
  const [ativos, setAtivos] = useState(config.avisosDiariosAtivos ?? true)

  const n = Number(hora.trim().replace(',', '.'))
  let erro: string | undefined
  if (hora.trim() === '' || !Number.isFinite(n)) erro = 'Informe um número.'
  else if (!Number.isInteger(n)) erro = 'Informe um número inteiro.'
  else if (n < faixa.minimo || n > faixa.maximo) erro = `Informe de ${faixa.minimo} a ${faixa.maximo}.`

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarNotificacoesConfiguracao({ horaDiariaBrt: n, avisosDiariosAtivos: ativos }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-notificacoes'] }); toast.success('Configuração das notificações salva.') },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card>
      <CardHeader title="Avisos diários" icon="ri-notification-3-line" />
      <CardBody className="space-y-5">
        <p className="text-13 text-muted">
          Os avisos de fatura vencendo, orçamento e lembretes de veículos saem uma vez por dia, depois da hora configurada.
          A conferência acontece de hora em hora; a mudança vale já na próxima.
        </p>
        <div className="flex items-center justify-between gap-4 rounded bg-surface2 px-4 py-3">
          <div>
            <p className="font-medium text-white">Avisos diários ativos</p>
            <p className="text-2xs text-muted">Desligado, a rotina diária não envia nada para ninguém.</p>
          </div>
          <Toggle label="" checked={ativos} onChange={setAtivos} disabled={salvar.isPending} />
        </div>
        <div className="max-w-xs">
          <Input
            label="Hora do envio (Horário de Brasília)" type="number" inputMode="numeric"
            min={faixa.minimo} max={faixa.maximo} step={1}
            value={hora} error={erro}
            onChange={(e) => setHora(e.target.value)}
          />
          <p className="mt-1 text-2xs text-muted">
            Horário de Brasília. Padrão {faixa.padrao}h, de {faixa.minimo}h a {faixa.maximo}h.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-2xs text-muted">{config.atualizadoEm ? `Última alteração em ${formatDate(config.atualizadoEm)}.` : 'Ainda não alterado (valendo os padrões).'}</p>
          <Button icon="ri-save-line" disabled={!!erro} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
        </div>
      </CardBody>
    </Card>
  )
}
